// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useEffect } from 'react';
import CategorySelector from './CategorySelector';
import VoiceRecorder from './VoiceRecorder';
import FeedbackResults from './FeedbackResults';
import TenantLoader from './TenantLoader';
import { applyTenantTheme } from '../tenantTheme';
import './DiscoverExperience.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const SLUG = 'discover';

const STEPS = {
  ACCESS: 'access',
  INTRO: 'intro',
  CATEGORY: 'category',
  RECORD: 'record',
  PROCESSING: 'processing',
  RESULTS: 'results',
};

export default function DiscoverExperience() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [accessCode, setAccessCode] = useState(() => sessionStorage.getItem('cc_access_code_discover') || '');
  const [step, setStep] = useState(STEPS.ACCESS);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [feedbackResult, setFeedbackResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/t/${SLUG}/config`)
      .then((res) => {
        if (!res.ok) throw new Error('Discover experience not configured');
        return res.json();
      })
      .then((data) => {
        setConfig(data);
        // Apply a distinct theme for discover
        applyTenantTheme(data.branding);
        if (!data.requiresAccessCode || sessionStorage.getItem('cc_access_code_discover')) {
          setStep(STEPS.INTRO);
        }
      })
      .catch((err) => setConfigError(err.message))
      .finally(() => setLoading(false));
  }, []);

  function handleAccessSubmit(e) {
    e.preventDefault();
    sessionStorage.setItem('cc_access_code_discover', accessCode);
    setStep(STEPS.INTRO);
  }

  function handleStart() {
    setStep(STEPS.CATEGORY);
  }

  function handleCategorySelected(category) {
    setSelectedCategory(category);
    setStep(STEPS.RECORD);
  }

  function handleProcessingStart() {
    setStep(STEPS.PROCESSING);
  }

  function handleRecordingComplete(result) {
    setFeedbackResult(result);
    setStep(STEPS.RESULTS);
  }

  function handleError(message) {
    setError(message);
    setStep(STEPS.RECORD);
  }

  function handleStartOver() {
    setStep(STEPS.INTRO);
    setSelectedCategory(null);
    setFeedbackResult(null);
    setError(null);
  }

  function handleBack() {
    if (step === STEPS.RECORD) {
      setStep(STEPS.CATEGORY);
      setSelectedCategory(null);
      setError(null);
    } else if (step === STEPS.CATEGORY) {
      setStep(STEPS.INTRO);
    }
  }

  if (loading) return <TenantLoader />;
  if (configError) {
    return (
      <div className="discover">
        <div className="discover__error-page">
          <h1>Not Available</h1>
          <p>The Discover experience is not currently configured. Please contact the administrator.</p>
        </div>
      </div>
    );
  }

  const apiBase = `${API_BASE}/t/${SLUG}`;
  const apiHeaders = config?.requiresAccessCode ? { 'X-Access-Code': accessCode } : {};

  return (
    <div className="discover">
      {/* Header */}
      <header className="discover__header">
        <div className="discover__header-inner">
          <div className="discover__brand">
            <span className="discover__brand-mark">STEWARD.GOLF</span>
            <span className="discover__brand-label">Discover</span>
          </div>
        </div>
      </header>

      <main className="discover__main">
        <div className="discover__container">

          {/* Access code gate */}
          {step === STEPS.ACCESS && (
            <div className="discover__gate">
              <h1 className="discover__gate-title">Welcome</h1>
              <p className="discover__gate-text">Enter your access code to begin sharing what matters most to you.</p>
              <form onSubmit={handleAccessSubmit} className="discover__gate-form">
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="ACCESS CODE"
                  maxLength={10}
                  className="discover__gate-input"
                  autoFocus
                />
                <button type="submit" className="discover__btn" disabled={!accessCode.trim()}>Continue</button>
              </form>
            </div>
          )}

          {/* Intro */}
          {step === STEPS.INTRO && (
            <div className="discover__intro">
              <h1 className="discover__intro-title">What matters most to you?</h1>
              <p className="discover__intro-text">
                We're listening to what prospective and current club enthusiasts value most in their club experience.
                Your voice helps shape the future of private clubs — no surveys, just a conversation.
              </p>
              <div className="discover__intro-how">
                <div className="discover__intro-step">
                  <span className="discover__intro-num">1</span>
                  <span>Pick a topic</span>
                </div>
                <div className="discover__intro-step">
                  <span className="discover__intro-num">2</span>
                  <span>Record your thoughts</span>
                </div>
                <div className="discover__intro-step">
                  <span className="discover__intro-num">3</span>
                  <span>We analyze & report</span>
                </div>
              </div>
              <button className="discover__btn discover__btn--large" onClick={handleStart}>
                Get Started
              </button>
              <p className="discover__intro-note">
                Takes under 2 minutes · No personal information collected · Professional language required
              </p>
            </div>
          )}

          {/* Category selection */}
          {step === STEPS.CATEGORY && (
            <div className="discover__categories">
              <button className="discover__back" onClick={handleBack}>← Back</button>
              <h2 className="discover__section-title">What would you like to share about?</h2>
              <p className="discover__section-sub">Select the topic that's most on your mind.</p>
              <CategorySelector categories={config.categories} onSelect={handleCategorySelected} />
            </div>
          )}

          {/* Recording */}
          {(step === STEPS.RECORD || step === STEPS.PROCESSING) && (
            <VoiceRecorder
              category={selectedCategory}
              isProcessing={step === STEPS.PROCESSING}
              onProcessingStart={handleProcessingStart}
              onComplete={handleRecordingComplete}
              onError={handleError}
              onBack={handleBack}
              error={error}
              apiBase={apiBase}
              apiHeaders={apiHeaders}
            />
          )}

          {/* Results */}
          {step === STEPS.RESULTS && (
            <div className="discover__results">
              <FeedbackResults
                result={feedbackResult}
                category={selectedCategory}
                onStartOver={handleStartOver}
              />
              <p className="discover__results-thanks">
                Thank you for sharing. Your perspective contributes to our understanding of what club members and prospects value most.
              </p>
            </div>
          )}

        </div>
      </main>

      <footer className="discover__footer">
        <p>Powered by Steward.golf · © 2026 Otyka LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}
